import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminTable from 'layouts/AdminTable';
import FormTextInput from 'components/FormTextInput/FormTextInput';
import FormControlButtons from 'components/FormControlButtons/FormControlButtons';
import { get, post, put } from 'helpers/apiHelpers';
import GridItem from 'components/Grid/GridItem';
import { Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { grayColor, whiteColor } from 'assets/jss/material-dashboard-pro-react';
import { useSelector } from 'react-redux';

const diacriticMaps = {
  pl: [
    ['ą', 'a'],
    ['ć', 'c'],
    ['ę', 'e'],
    ['ł', 'l'],
    ['ń', 'n'],
    ['ó', 'o'],
    ['ś', 's'],
    ['ź', 'z'],
    ['ż', 'z'],
  ],
  lv: [
    ['ā', 'a'],
    ['č', 'c'],
    ['ē', 'e'],
    ['ģ', 'g'],
    ['ī', 'i'],
    ['ķ', 'k'],
    ['ļ', 'l'],
    ['ņ', 'n'],
    ['š', 's'],
    ['ū', 'u'],
    ['ž', 'z'],
  ],
};

const useStyles = makeStyles({
  wrapper: { position: 'relative', padding: '0' },
  dropdownWrapper: {
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    position: 'absolute',
    top: '60px',
    width: '100%',
    backgroundColor: whiteColor,
    maxHeight: '250px',
    overflowY: 'auto',
  },
  listWrapper: {
    listStyle: 'none',
    padding: '0',
    margin: '0',
  },
  listTitle: { fontSize: '12px', fontWeight: '500' },
  listItem: {
    fontSize: '12px',
    fontWeight: '400',
    color: grayColor[20],
    padding: '8px 0 4px 0',
  },
});

const Tags = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const params = useParams();
  const classes = useStyles();

  const [name, setName] = useState('');
  const [allTags, setAllTags] = useState([]);
  const [isDropdownShown, setIsDropdownShown] = useState(false);

  const { adminDefaultLanguage } = useSelector(
    ({ Brands }) => Brands.brand.multinational
  );

  const tagId = params.id;
  const isEdit = !!tagId;

  const fetchValue = async () => {
    try {
      const data = await get(`/tags/${tagId}`);
      setName(data.value);
      // eslint-disable-next-line no-empty -- FIXME
    } catch {}
  };

  const fetchAllTags = async () => {
    try {
      const data = await get('/tags', { pagination: false });

      setAllTags(data['hydra:member'] || []);
      // eslint-disable-next-line no-empty -- FIXME
    } catch {}
  };

  const normalizedValue = value => {
    let result = value.trim().toLowerCase();
    const map = diacriticMaps[adminDefaultLanguage] || [];

    map.forEach(([from, to]) => {
      result = result.replace(new RegExp(from, 'g'), to);
    });

    return result;
  };

  const sortedTags = allTags.filter(tag => {
    const inputValue = normalizedValue(name || '');

    if (inputValue.length < 2) {
      return null;
    }

    const value = normalizedValue(tag?.value || '');

    if (value.includes(inputValue) && (!isEdit || value !== inputValue)) {
      return tag;
    }

    return null;
  });

  useEffect(() => {
    if (isEdit) {
      fetchValue();
    }
    fetchAllTags();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      toast.error(t('form.nameFieldCannotBeEmpty'));
      return;
    }

    const data = { value: trimmedName };

    try {
      if (isEdit) {
        await put(`/tags/${tagId}`, data);
      } else {
        await post('/tags', data);
      }

      history.push('/admin/tags');
      // eslint-disable-next-line no-empty -- FIXME
    } catch {}
  };

  return (
    <AdminTable title={isEdit ? t('form.editValue') : t('form.addNewValue')}>
      <GridItem xs={6} className={classes.wrapper}>
        <FormTextInput
          label={`${t('form.field.name')}*`}
          value={name}
          handleChange={e => {
            const { value } = e.target;
            setName(value);

            if (value.length >= 2) {
              setIsDropdownShown(true);
            }
          }}
          onFocus={() => {
            if (name.trim().length >= 2 && sortedTags.length > 0) {
              setIsDropdownShown(true);
            }
          }}
          onBlur={() => {
            setIsDropdownShown(false);
          }}
        />

        {sortedTags.length > 0 && isDropdownShown && (
          <GridItem className={classes.dropdownWrapper} xs={11}>
            <p className={classes.listTitle}>
              {t('dictionary.tags.existingTags')}
            </p>
            <Divider className={classes.divider} />
            <ul className={classes.listWrapper}>
              {sortedTags.map(tag => (
                <li key={tag.id} className={classes.listItem}>
                  {tag.value}
                </li>
              ))}
            </ul>
          </GridItem>
        )}
      </GridItem>

      <FormControlButtons
        discardText={t('form.cancel')}
        submitText={t('form.save')}
        cancelPath="/admin/tags"
        handleSubmit={submit}
      />
    </AdminTable>
  );
};

export default Tags;
